import { parse } from 'csv-parse/browser/esm/sync';
import * as React from 'react';
import { CSVRow, Entity, FieldType, getFieldArray, Row, validate } from 'gossamer-unified';

export interface CSVUploadInstructionsProps {
  entity: Entity<Row>;
}

const MAX_PROCESSED_ROWS = 1000;
const BLANK_ROW_TEXT = 'blank row - ignored';
const ZIP_FILE_SIGNATURE = 'PK\u0003\u0004';

export const getSpreadsheetColumn = (index: number) => {
  const leftLetter = Math.floor(index / 26);
  const rightLetter = index % 26;
  return (leftLetter > 0 ? String.fromCharCode(64 + leftLetter) : '') + String.fromCharCode(65 + rightLetter);
};

export const CSVUploadInstructions = (props: CSVUploadInstructionsProps): React.JSX.Element => {
  return (
    <div>
      The columns in the uploaded CSV file will be used as follows:
      <ul>
        {getFieldArray(props.entity)
          .filter(([field, _fieldId]) => field.visible !== false)
          .map(([field, fieldId], index) => (
            <li key={fieldId}>
              {getSpreadsheetColumn(index)}: <b>{field.label}</b> {field.mandatory ? '(required) ' : '(optional) '}
              {field.type === FieldType.Options &&
                '(must be one of: ' +
                  field.options
                    .filter((opt) => opt.active !== false)
                    .map((opt) => opt.id)
                    .join(', ') +
                  ') '}
              {field.description || ''}
            </li>
          ))}
      </ul>
      Other columns will be ignored. There is no header row expected - all rows will be processed. The maximum number of
      rows is {MAX_PROCESSED_ROWS}. Any subsequent rows will be ignored.
    </div>
  );
};

export const getRowsFromRawString = <T extends Row>(entity: Entity<T>, rawData: string): CSVRow<T>[] => {
  const fieldIds = getFieldArray(entity)
    .filter(([field, _fieldId]) => field.visible !== false)
    .map(([_field, fieldId]) => fieldId);
  if (rawData.startsWith(ZIP_FILE_SIGNATURE)) {
    throw new Error('This looks like an Excel file - save it as CSV and try again');
  }
  let rows: CSVRow<T>[];
  try {
    rows = parse<CSVRow<T>, Record<string, string>>(rawData, {
      columns: fieldIds,
      relax_column_count: true,
      skip_empty_lines: true,
      to: MAX_PROCESSED_ROWS,
    });
  } catch (e) {
    throw new Error(`This file is not a valid CSV file. ${e instanceof Error ? e.message : String(e)}`);
  }
  return rows.map((row) => {
    const mutableRow = row as Record<string, string | null>;
    Object.keys(row).forEach((key) => {
      if (mutableRow[key] === '') {
        mutableRow[key] = null;
      } else if (typeof mutableRow[key] === 'string') {
        mutableRow[key] = mutableRow[key].trim();
      }
    });
    return row;
  });
};

export const makeValidator =
  <T extends Row>(entity: Entity<T>) =>
  (row: CSVRow<T>): string[] => {
    const out: string[] = [];
    const rowValues = row as Record<string, unknown>;
    Object.keys(entity.fields).forEach((fieldId) => {
      const field = entity.fields[fieldId as keyof T];
      const stdValidation = validate(field, rowValues[fieldId] as string | number | boolean | null | undefined);
      if (stdValidation) {
        out.push(`${field.label}: '${rowValues[fieldId] || ''}' ${stdValidation}`);
      }
    });
    return out;
  };

export const identifyErrors = <T extends Row>(
  values: CSVRow<T>[],
  validationFunction: (row: CSVRow<T>) => string[],
): string[][] => {
  return values.map((row: CSVRow<T>) => {
    if (row._ignore) {
      return [BLANK_ROW_TEXT];
    }
    return validationFunction(row);
  });
};

const messagePreamble = (num: number, str: string) =>
  ['There', num === 1 ? 'is' : 'are', String(num), str, num === 1 ? 'row' : 'rows'].join(' ');

type Status = 'GOOD' | 'BLANK' | 'BAD';

interface ReportUploadStatusProps {
  errors: string[][];
}

const ERROR_DETAIL_LIMIT = 100;

export const ReportUploadStatus = (props: ReportUploadStatusProps): React.JSX.Element => {
  const rowStatus: Status[] = props.errors.map((row) =>
    row.length === 0 ? 'GOOD' : row.length === 1 && row[0] === BLANK_ROW_TEXT ? 'BLANK' : 'BAD',
  );
  const goodRowCount = rowStatus.filter((status) => status === 'GOOD').length;
  const blankRowCount = rowStatus.filter((status) => status === 'BLANK').length;
  const badRowCount = rowStatus.filter((status) => status === 'BAD').length;

  const lineReporter = (status: Status) => (prev: number[], curr: Status, index: number) => {
    if (curr === status) prev.push(index + 1);
    return prev;
  };
  const goodRowLines = rowStatus.reduce(lineReporter('GOOD'), []);
  const blankRowLines = rowStatus.reduce(lineReporter('BLANK'), []);
  const badRowLines = rowStatus.reduce(lineReporter('BAD'), []);
  const errorDetail: string[] = [];
  props.errors.forEach((errors, index) => {
    for (let i = 0; i < errors.length; i += 1) {
      if (errorDetail.length < ERROR_DETAIL_LIMIT && errors[i] !== BLANK_ROW_TEXT) {
        errorDetail.push('Line ' + (index + 1) + ': ' + errors[i]);
      }
    }
  });
  return (
    <div>
      <ul>
        <li>
          <b>{messagePreamble(goodRowCount, 'valid')}</b>
          {goodRowCount > 0 && '. These are line(s): ' + goodRowLines.join(', ') + '. These will be uploaded.'}
        </li>
        <li>
          <b>{messagePreamble(blankRowCount, 'blank')}</b>
          {blankRowCount > 0 && '. These are line(s): ' + blankRowLines.join(', ') + '. These will not be uploaded.'}
        </li>
        <li>
          <b>{messagePreamble(badRowCount, 'invalid')}</b>
          {badRowCount > 0 && '. These are line(s): ' + badRowLines.join(', ') + '. These will not be uploaded.'}
        </li>
        {errorDetail.map((errorDetail) => (
          <li key={errorDetail}>{errorDetail}</li>
        ))}
        {errorDetail.length === ERROR_DETAIL_LIMIT && <li>(Remaining errors skipped)</li>}
      </ul>
    </div>
  );
};
